import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import CustomModal from "components/common/CustomModal";
import { theme } from "styles/theme";
import {
  GuideDTO,
  GuideName,
  GuideVersionConflictError,
  listGuides,
  putGuide,
} from "api/taskGuidesApi";
import { captureException } from "utils/sentry";

interface GuideEditorModalProps {
  open: boolean;
  onClose: () => void;
}

// 마크다운 툴바에서 이미지 삽입 커맨드 제거 — 가이드는 텍스트만 다룬다.
const EDITOR_COMMANDS = commands
  .getCommands()
  .filter((c) => c.name !== "image");

const GUIDE_ORDER: GuideName[] = ["tone", "principles"];
const GUIDE_LABELS: Record<GuideName, string> = {
  tone: "톤 (tone.md)",
  principles: "제작 원칙 (principles.md)",
};
// 탭별 목적 한 줄 설명 — 어떤 가이드를 편집 중인지 살짝 안내.
const GUIDE_DESCRIPTIONS: Record<GuideName, string> = {
  tone: "어조·말투·표현 — 활동지가 어떻게 들리는가",
  principles: "제작 원칙 — 구성·흐름·분량 등 무엇을 어떻게 쓰는가",
};

function guideErrorText(e: unknown): string {
  return e instanceof Error ? e.message : "오류가 발생했어요";
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export default function GuideEditorModal({
  open,
  onClose,
}: GuideEditorModalProps) {
  const [guides, setGuides] = useState<Partial<Record<GuideName, GuideDTO>>>(
    {}
  );
  const [drafts, setDrafts] = useState<Record<GuideName, string>>({
    tone: "",
    principles: "",
  });
  const [activeTab, setActiveTab] = useState<GuideName>("tone");
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<GuideName | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 열릴 때마다 최신 가이드 로드 (저장 없이 닫았다 다시 열면 서버 상태로 리셋).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listGuides()
      .then((list) => {
        if (cancelled) return;
        const byName: Partial<Record<GuideName, GuideDTO>> = {};
        const nextDrafts: Record<GuideName, string> = {
          tone: "",
          principles: "",
        };
        for (const g of list) {
          byName[g.name] = g;
          nextDrafts[g.name] = g.content;
        }
        setGuides(byName);
        setDrafts(nextDrafts);
      })
      .catch((e) => {
        if (cancelled) return;
        // 가이드 로드 실패는 화면에만 보이고 사라지므로 Sentry 로도 보고한다
        // (백오피스 — 간헐적 네트워크/서버 실패 추적). "Failed to fetch" 류는
        // 서버에 도달 못한 케이스라 백엔드 로그엔 안 남고 FE 만 잡을 수 있다.
        captureException(e, {
          tags: { feature: "guide-editor", op: "listGuides" },
        });
        setError(guideErrorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const applyGuide = (g: GuideDTO) => {
    setGuides((prev) => ({ ...prev, [g.name]: g }));
    setDrafts((prev) => ({ ...prev, [g.name]: g.content }));
  };

  const handleSave = async (name: GuideName) => {
    const base = guides[name];
    if (!base) return;
    setSaving(name);
    setError(null);
    try {
      applyGuide(await putGuide(name, drafts[name], base.version));
    } catch (e) {
      if (e instanceof GuideVersionConflictError) {
        // 다른 editor 가 먼저 저장 — 최신본으로 갱신하고 알린다.
        applyGuide(e.current);
        setError(
          "다른 사람이 먼저 저장했어요. 최신본을 불러왔으니 다시 확인 후 저장해주세요."
        );
      } else {
        // 충돌(예상된 흐름) 외 저장 실패는 Sentry 로 보고.
        captureException(e, {
          tags: { feature: "guide-editor", op: "putGuide", guide: name },
        });
        setError(guideErrorText(e));
      }
    } finally {
      setSaving(null);
    }
  };

  const active = guides[activeTab];
  const activeDraft = drafts[activeTab] ?? "";
  const isOverride = active?.source === "override";
  const savingActive = saving === activeTab;
  const dirtyActive = active ? activeDraft !== active.content : false;
  const emptyActive = activeDraft.trim().length === 0;
  const canSave = dirtyActive && !emptyActive && !savingActive && !loading;

  // 에디터가 다이얼로그의 남은 세로 공간을 가득 채우도록 px 높이를 계산한다.
  // 래퍼의 clientHeight 가 아니라 다이얼로그 실측 rect 를 기준으로 잡아 flex 전파가
  // 어긋나도 안전하게 동작한다. 퍼센트/100% 를 넘기면 하단 dragbar 그립이 비활성되므로
  // 반드시 number(px) 로 줘야 한다.
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(460);
  useLayoutEffect(() => {
    if (!open) return;
    let raf = 0;
    let ro: ResizeObserver | null = null;

    // 다이얼로그 rect 를 기준으로 에디터가 채울 높이를 계산한다. 모달 포털이
    // 아직 측정 불가한 첫 프레임에 작게 잡히던 문제를 막기 위해, 유효한 측정값을
    // 얻을 때까지 프레임마다 재시도하고 그 시점에 ResizeObserver 를 붙인다.
    const measure = (): boolean => {
      const wrapper = editorWrapperRef.current;
      if (!wrapper) return false;
      const dialog = wrapper.closest<HTMLElement>('[role="dialog"]');
      if (!dialog) return false;
      const dialogRect = dialog.getBoundingClientRect();
      if (dialogRect.height === 0) return false;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      // 에디터 아래로 남겨둘 영역: 다이얼로그 하단 패딩(32) + 액션 버튼(46)
      // + 바깥 Stack 간격(24) + 에러 영역 + 안전 여백.
      const reservedBelow = 32 + 46 + 24 + (error ? 40 : 0) + 8;
      const next = Math.floor(dialogRect.bottom - wrapperTop - reservedBelow);
      if (next > 0) setEditorHeight(Math.max(280, next));
      if (!ro) {
        ro = new ResizeObserver(() => measure());
        ro.observe(dialog);
      }
      return true;
    };

    let attempts = 0;
    const tick = () => {
      if (!measure() && attempts++ < 60) raf = requestAnimationFrame(tick);
    };
    tick();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      ro?.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [open, error, activeTab]);

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box component="span">가이드 편집</Box>
          <IconButton
            variant="plain"
            size="sm"
            color="neutral"
            aria-label="닫기"
            onClick={onClose}
            sx={{ minHeight: "32px", minWidth: "32px", borderRadius: "8px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      }
      subtitle="에이전트가 참고하는 톤·제작 원칙 가이드입니다. 저장하면 다음 작성/검토 턴부터 반영됩니다."
      dialogProps={{
        sx: {
          width: "96vw",
          maxWidth: "96vw",
          height: "96vh",
          maxHeight: "96vh",
        },
      }}
      actions={[
        {
          label: "저장",
          variant: "Solid",
          color: "primary",
          onClick: () => handleSave(activeTab),
          buttonProps: { loading: savingActive, disabled: !canSave },
        },
      ]}
    >
      {/* 톤/구조 탭 — 워크스페이스의 편집/미리보기/코드 탭과 동일 스타일 */}
      <Box
        sx={{
          display: "flex",
          gap: "4px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          mb: "12px",
        }}
      >
        {GUIDE_ORDER.map((name) => {
          const isActive = activeTab === name;
          return (
            <Box
              key={name}
              onClick={() => setActiveTab(name)}
              sx={{
                px: "10px",
                py: "6px",
                cursor: "pointer",
                borderBottom: `2px solid ${
                  isActive ? theme.vars.palette.Content.Strong : "transparent"
                }`,
                color: isActive
                  ? theme.vars.palette.Content.Strong
                  : theme.vars.palette.Content.Weak,
                fontWeight: isActive ? 700 : 500,
                fontSize: "14px",
                userSelect: "none",
              }}
            >
              {GUIDE_LABELS[name]}
            </Box>
          );
        })}
      </Box>

      <Typography
        level="body-xs"
        sx={{ color: theme.vars.palette.Content.Medium, mb: "8px" }}
      >
        {GUIDE_DESCRIPTIONS[activeTab]}
      </Typography>

      {/* 상태 칩 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minHeight: "24px",
          mb: "8px",
        }}
      >
        <Chip
          size="sm"
          variant="soft"
          color={isOverride ? "primary" : "neutral"}
        >
          {isOverride ? "수정됨" : "기본값"}
        </Chip>
        {active?.updated_at && (
          <Typography
            level="body-xs"
            sx={{ color: theme.vars.palette.Content.Medium }}
          >
            마지막 저장 {formatTime(active.updated_at)}
          </Typography>
        )}
      </Box>

      {/* 마크다운 에디터 (편집 + 라이브 미리보기) */}
      {/* height 를 px(숫자)로 둬야 하단 dragbar 그립이 유지된다 (퍼센트/vh 면 비활성). */}
      {/* 글자·툴바 아이콘 기본값이 작아 CSS 로 키운다. textarea 와 하이라이트 pre 는
          폰트/행간을 동일하게 맞춰야 커서 정렬이 어긋나지 않는다. */}
      <Box
        ref={editorWrapperRef}
        data-color-mode="light"
        sx={{
          "& .w-md-editor-toolbar svg": { width: 18, height: 18 },
          "& .w-md-editor-toolbar ul > li > button": {
            height: "auto",
            padding: "5px 6px",
          },
          "& .w-md-editor-text-pre, & .w-md-editor-text-input, & .w-md-editor-text-pre > code":
            {
              fontSize: "15px !important",
              lineHeight: "1.7 !important",
            },
          "& .wmde-markdown": { fontSize: "15px" },
        }}
      >
        <MDEditor
          value={activeDraft}
          onChange={(v) =>
            setDrafts((prev) => ({ ...prev, [activeTab]: v ?? "" }))
          }
          height={editorHeight}
          maxHeight={Math.max(editorHeight, 2000)}
          preview="live"
          commands={EDITOR_COMMANDS}
        />
      </Box>

      {error && (
        <Typography
          level="body-sm"
          sx={{ mt: "8px", color: theme.vars.palette.danger[500] }}
        >
          {error}
        </Typography>
      )}
    </CustomModal>
  );
}
